import { useEffect, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { dispatchOrderApi, type DispatchNote, type DispatchOrder } from "../../api/dispatchOrders";
import type { Order } from "../../api/orders";
import type { ApiError } from "../../api/client";
import { LotQualityBadge } from "../LotQualityBadge";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../Toast";
import { DispatchErpStatus } from "./DispatchErpStatus";
import { NoteInvoices } from "./NoteInvoices";
import { formatDate, formatNumber } from "../../lib/format";
import { card, cardTitle as title, hint, item, muted, mono, nested as noteBox, row } from "./orderCardStyles";

/**
 * What follows an approved order: its dispatch orders, and what the ERP did with them. The order is in the ERP as a
 * pro forma invoice (and, with deferred payment, a customer's order); here sales opens the dispatch order with the
 * order's lines and delivery, adds the vehicle and driver and sends it to quality, which picks the lot of every line.
 * Under each dispatch order stand the dispatch notes the ERP issued on it, with the lot of every line, and the
 * invoices that bill them, as the import brings them; a lot quality did not release for the article is flagged, since
 * the ERP picks the lot itself when the warehouse pulls the order into a dispatch note.
 */
export function OrderDispatch({ order }: { order: Order }) {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  // An order brought over from the ERP is only viewed here: its dispatch orders are made in the ERP.
  const mayOpen = hasPermission("dispatch_orders:manage") && !order.fromErp;
  const [made, setMade] = useState<DispatchOrder[] | null>(null);
  const [notes, setNotes] = useState<DispatchNote[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    dispatchOrderApi.forOrder(order.id)
      .then((rows) => { if (live) setMade(rows); })
      .catch(() => { if (live) setMade([]); });
    dispatchOrderApi.notesForOrder(order.id)
      .then((rows) => { if (live) setNotes(rows); })
      .catch(() => { if (live) setNotes([]); });
    return () => { live = false; };
  }, [order.id]);

  async function open() {
    setBusy(true);
    try {
      const created = await dispatchOrderApi.createFromOrder(order.id);
      navigate(`/sales/dispatch-orders?open=${created.id}`);
    } catch (e) {
      toast.error((e as ApiError).message);
      setBusy(false);
    }
  }

  // Nothing to say on an order from the ERP that has no dispatch order, and nothing to open there either.
  if (order.fromErp && !made?.length) return null;

  return (
    <section style={card}>
      <h2 style={title}>{t("orderErp.dispatchTitle", "Dispatch and invoicing")}</h2>
      {made && made.length > 0 ? (
        <ul style={list}>
          {made.map((d) => (
            <li key={d.id} style={item}>
              <div style={row}>
                <Link to={`/sales/dispatch-orders?open=${d.id}`}>{d.number}</Link>
                <span style={muted}>{formatDate(d.issueDate)}</span>
                <span style={muted}>{t(`dispatchRelease.status.${d.status}`, d.status)}</span>
                <DispatchErpStatus order={d} compact />
              </div>
              {notes.filter((n) => n.dispatchOrderId === d.id).map((n) => (
                <NoteBlock key={n.id} note={n} released={releasedLots(d)} />
              ))}
            </li>
          ))}
        </ul>
      ) : (
        <p style={hint}>
          {t("orderErp.dispatchNone", "No dispatch order yet. It opens with the order's lines; quality picks the lot of each.")}
        </p>
      )}
      {mayOpen && (
        <div>
          <button type="button" className="btn primary" onClick={() => void open()} disabled={busy || made === null}>
            {t("orderErp.dispatchCreate", "Create dispatch order")}
          </button>
        </div>
      )}
    </section>
  );
}

/** The lots quality released on a dispatch order, by article: their ids, and their labels to name them. */
type Released = Map<string, { ids: Set<string>; labels: string[] }>;

function releasedLots(d: DispatchOrder): Released {
  const out: Released = new Map();
  for (const l of d.lines ?? []) {
    if (!l.released || !l.goodsLotId || !l.erpProductId) continue;
    const r = out.get(l.erpProductId) ?? { ids: new Set<string>(), labels: [] };
    r.ids.add(l.goodsLotId);
    if (l.lotDesignation && !r.labels.includes(l.lotDesignation)) r.labels.push(l.lotDesignation);
    out.set(l.erpProductId, r);
  }
  return out;
}

function NoteBlock({ note, released }: { note: DispatchNote; released: Released }) {
  const { t, i18n } = useTranslation();
  const jezik = i18n.resolvedLanguage;
  return (
    <div style={noteBox} data-testid="dispatch-note">
      <div style={row}>
        <span>{t("orderErp.note", "Dispatch note {{number}}", { number: note.number })}</span>
        <span style={muted}>{formatDate(note.issueDate)}</span>
      </div>
      {note.lines.map((l, i) => {
        const pusteni = l.erpProductId ? released.get(l.erpProductId) : undefined;
        const drugi = pusteni !== undefined && l.lotId !== null && !pusteni.ids.has(l.lotId);
        return (
          <div key={i} style={row}>
            <span>{l.productName ?? l.erpProductId ?? "—"}</span>
            <span style={muted}>{formatNumber(l.quantity, jezik)} {l.unit ?? ""}</span>
            {l.lot && (
              <span style={muted}>
                {t("dispatchOrders.col.lot", "Lot")} <span style={mono}>{l.lot}</span>
              </span>
            )}
            {/* Only a status quality gave: a lot that left before the portal had statuses was not "unprocessed". */}
            {l.lotQualityStatus && <LotQualityBadge status={l.lotQualityStatus} />}
            {drugi && (
              <span style={warn} data-testid="lot-not-released">
                {t("orderErp.lotNotReleased", "quality released {{lots}}", { lots: pusteni.labels.join(", ") })}
              </span>
            )}
          </div>
        );
      })}
      <NoteInvoices note={note} />
    </div>
  );
}

// An order can go out on dozens of dispatch orders; the list scrolls instead of stretching the page.
const list: CSSProperties = {
  listStyle: "none",
  margin: 0,
  padding: 0,
  display: "flex",
  flexDirection: "column",
  gap: 10,
  maxHeight: 560,
  overflowY: "auto",
};
const warn: CSSProperties = { color: "var(--warn)", fontSize: 12 };
